import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { authApi } from '../api/auth';
import { getVerificationData, clearVerificationData } from '../utils/sessionStorage';
import { AUTH_CONSTANTS } from '../constants/auth';
import type { VerificationSessionData } from '../types';
import { AuthCard, AuthScreen, AuthTitle, shakeClass } from '../components/auth/AuthScreen';
import { Banner, Button, SkeletonRows, cx } from '../components/ui';

const LEN = AUTH_CONSTANTS.VERIFICATION_CODE_LENGTH;
const RESEND_COOLDOWN_S = 30;
const blank = () => Array.from({ length: LEN }, () => '');

export const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState<VerificationSessionData | null>(null);
  const [code, setCode] = useState<string[]>(blank);
  const [status, setStatus] = useState<{
    loading: boolean;
    error: string;
    success: string;
    attempts: number;
  }>({
    loading: false,
    error: '',
    success: '',
    attempts: AUTH_CONSTANTS.MAX_VERIFICATION_ATTEMPTS,
  });
  const [verified, setVerified] = useState(false);
  const [shake, setShake] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(AUTH_CONSTANTS.VERIFICATION_EXPIRY_MINUTES * 60);
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Use type-safe session storage utility
    const stored = getVerificationData();

    if (!stored) {
      navigate('/login', { replace: true, state: { error: 'Session expired.' } });
      return;
    }

    setSessionData(stored);
  }, [navigate]);

  useEffect(() => {
    if (timeRemaining <= 0) {
      setStatus((s) => ({ ...s, error: 'Code expired.' }));
      return;
    }
    const timer = setInterval(() => setTimeRemaining((p) => p - 1), 1000);
    return () => clearInterval(timer);
  }, [timeRemaining]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleVerify = async (finalCode?: string) => {
    if (!sessionData) return;

    const codeToVerify = finalCode || code.join('');
    if (codeToVerify.length !== LEN) {
      setStatus((s) => ({ ...s, error: `Enter all ${LEN} characters.` }));
      setShake((n) => n + 1);
      return;
    }
    if (status.loading) return;

    setStatus((s) => ({ ...s, loading: true, error: '' }));
    try {
      const res = await authApi.verifyOnboarding(codeToVerify, sessionData.sessionToken);

      // SUCCESS: Clean up verification data
      clearVerificationData();

      localStorage.setItem('onboarding_token', res.onboardingToken);
      localStorage.setItem('onboarding_userId', res.userId);
      localStorage.setItem('onboarding_email', sessionData.email);
      setVerified(true);
      window.location.href = '/create-church';
    } catch (error: unknown) {
      // Proper error type handling with type guard
      const msg =
        error instanceof Error && 'response' in error
          ? (error as any).response?.data?.message || 'Verification failed. Please try again.'
          : 'Verification failed. Please try again.';

      // Decrease attempts on every failed verification
      const newAttempts = status.attempts - 1;

      setStatus({
        loading: false,
        error: newAttempts === 0 ? 'Account locked. Please return to login and try again.' : msg,
        success: '',
        attempts: Math.max(0, newAttempts),
      });
      setShake((n) => n + 1);

      if (newAttempts > 0) {
        setCode(blank());
        inputRefs.current[0]?.focus();
      }
    }
  };

  const handleInputChange = (index: number, value: string) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (!cleaned && value !== '') return;

    // Multi-character input (browser autofill / autocomplete="one-time-code")
    if (cleaned.length > 1) {
      const newCode = blank();
      cleaned
        .slice(0, LEN)
        .split('')
        .forEach((c, idx) => {
          newCode[idx] = c;
        });
      setCode(newCode);
      if (cleaned.length >= LEN) {
        handleVerify(newCode.join(''));
      } else {
        inputRefs.current[cleaned.length]?.focus();
      }
      return;
    }

    const newCode = [...code];
    newCode[index] = cleaned;
    setCode(newCode);
    if (status.error) setStatus((s) => ({ ...s, error: '' }));

    if (cleaned && index < LEN - 1) inputRefs.current[index + 1]?.focus();
    if (newCode.every((c) => c !== '')) handleVerify(newCode.join('')); // Auto-submit
  };

  const handleResend = async () => {
    if (!sessionData || cooldown > 0) return;

    setStatus((s) => ({ ...s, loading: true, error: '' }));
    try {
      const res = await authApi.resendVerification(sessionData.sessionToken);
      setSessionData({
        ...sessionData,
        sessionToken: res.sessionToken,
      });
      setStatus({
        loading: false,
        error: '',
        success: 'New code sent.',
        attempts: AUTH_CONSTANTS.MAX_VERIFICATION_ATTEMPTS,
      });
      setTimeRemaining(AUTH_CONSTANTS.VERIFICATION_EXPIRY_MINUTES * 60);
      setCooldown(RESEND_COOLDOWN_S);
      setCode(blank());
      inputRefs.current[0]?.focus();
    } catch {
      setStatus((s) => ({ ...s, loading: false, error: 'Failed to resend code.' }));
    }
  };

  if (!sessionData) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center p-4">
        <SkeletonRows rows={2} className="w-72" />
      </div>
    );
  }

  const locked = status.attempts === 0 || timeRemaining === 0;
  const mins = Math.floor(timeRemaining / 60);
  const secs = (timeRemaining % 60).toString().padStart(2, '0');

  if (verified) {
    return (
      <AuthScreen>
        <AuthCard className="px-[26px] py-[34px] text-center">
          <div className="w-14 h-14 rounded-full bg-ok-bg text-ok flex items-center justify-center mx-auto mb-[18px]">
            <Check size={26} strokeWidth={2.2} aria-hidden="true" />
          </div>
          <h1 className="text-[22px] font-semibold tracking-[-0.02em]">Email verified</h1>
          <p className="text-[14px] text-t3 mt-2 leading-[1.55]">Setting up your church next…</p>
        </AuthCard>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <AuthTitle
        title="Check your email"
        sub={
          <>
            Enter the {LEN}-character code we sent to
            <br />
            <span className="text-t1 font-medium">{sessionData.email}</span>
          </>
        }
      />
      <AuthCard className="p-[26px]">
        {status.success && (
          <Banner tone="ok" role="status" className="mb-4">
            {status.success}
          </Banner>
        )}
        <div
          role="group"
          aria-label="Verification code"
          className={cx('grid gap-2', shakeClass(shake))}
          style={{ gridTemplateColumns: `repeat(${LEN}, minmax(0, 1fr))` }}
        >
          {code.map((digit, i) => (
            <input
              key={i}
              ref={(el) => {
                inputRefs.current[i] = el;
              }}
              type="text"
              inputMode="text"
              maxLength={1}
              value={digit}
              aria-label={`Character ${i + 1}`}
              aria-invalid={!!status.error || undefined}
              onChange={(e) => handleInputChange(i, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Backspace' && !code[i] && i > 0) {
                  inputRefs.current[i - 1]?.focus();
                }
              }}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = e.clipboardData
                  .getData('text')
                  .toUpperCase()
                  .replace(/[^A-Z0-9]/g, '')
                  .slice(0, LEN);
                if (pasted.length > 0) {
                  const newCode = blank();
                  pasted.split('').forEach((c, idx) => {
                    newCode[idx] = c;
                  });
                  setCode(newCode);
                  if (pasted.length === LEN) {
                    handleVerify(newCode.join(''));
                  } else {
                    inputRefs.current[pasted.length]?.focus();
                  }
                }
              }}
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              className="wp-field h-14 text-center font-mono text-[22px] font-bold uppercase"
              style={{ borderColor: status.error ? 'rgba(208,48,69,.6)' : digit ? 'var(--t5)' : undefined }}
              disabled={status.loading || locked}
            />
          ))}
        </div>
        <div className="text-[12.5px] text-acc mt-2.5 min-h-4 text-center" role="alert">
          {status.error}
        </div>
        <div className="flex justify-between text-[12.5px] text-t4 mt-1 mb-3">
          <span className={cx('font-mono', timeRemaining < 60 && 'text-acc font-bold')}>
            {mins}:{secs}
          </span>
          <span>{status.attempts === 0 ? 'Locked' : `${status.attempts} attempts left`}</span>
        </div>
        {locked ? (
          <Button size="xl" shape="form" block onClick={() => (window.location.href = '/login')}>
            Return to login
          </Button>
        ) : (
          <Button size="xl" shape="form" block glow loading={status.loading} onClick={() => handleVerify()}>
            Verify email
          </Button>
        )}
      </AuthCard>
      {!locked && (
        <div className="text-[13.5px] text-t4 mt-6 flex gap-1.5">
          Didn't get it?
          <button
            type="button"
            onClick={handleResend}
            disabled={status.loading || cooldown > 0}
            className="text-link hover:text-acc disabled:text-t5 disabled:cursor-not-allowed"
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
          </button>
        </div>
      )}
    </AuthScreen>
  );
};
